/**
 * Report card arithmetic, extracted from FacsimileDetailsPV.
 *
 * Every function here is pure and takes the period list from the grading scheme,
 * so the same code serves four-quarter and three-term school years. Given four
 * periods the results are identical to the inline code these replaced,
 * including its quirks — historical cards have to reprint unchanged, so the
 * quirks are specified in the tests rather than fixed:
 *
 *   - A subject's final grade is 0, not blank, until the last period is graded.
 *   - Conduct's final is "" until the last period is graded.
 *   - The general average blends subjects 90%, conduct 5% and the club 5%.
 *     That is a school rule, not DepEd's.
 *   - An empty conduct or club grade contributes 0 to that blend rather than
 *     voiding it. A learner in no club therefore scores nothing for that 5%,
 *     which the registrar chose knowingly.
 *   - MAPEH zeroes a whole period if any one component subject is ungraded.
 */

/** Sums a grade record across the scheme's periods. */
function periodValues(grade, periods) {
  return periods.map(function (p) {
    const v = Number(grade[p.dbField]);
    return isNaN(v) ? 0 : v;
  });
}

/**
 * Average of every period, rounded. Returns 0 while the last period is
 * ungraded, which is what the card treats as "nothing to show yet".
 */
export function subjectFinalGrade(grade, periods) {
  if (!grade || !periods || periods.length === 0) return 0;

  const lastField = periods[periods.length - 1].dbField;
  if (!Number(grade[lastField])) return 0;

  const values = periodValues(grade, periods);
  const total = values.reduce(function (a, b) { return a + b; }, 0);
  return Math.round(total / periods.length);
}

/**
 * MAPEH has no Subject record of its own; it is synthesised from whichever of
 * Music, Arts, Physical Education, Health, Music and Arts, and PEH are assigned
 * to the section. A period is zeroed if any component is ungraded for it, so a
 * partial MAPEH never shows a misleadingly high figure.
 *
 * Returns { periodGrades: number[], finalGrade: number }.
 */
export function mapehComposite(componentGrades, periods) {
  const empty = {
    periodGrades: periods.map(function () { return 0; }),
    finalGrade: 0,
  };
  if (!componentGrades || componentGrades.length === 0) return empty;

  const totals = periods.map(function () { return 0; });
  const hasUngraded = periods.map(function () { return false; });

  componentGrades.forEach(function (grade) {
    if (grade == null) return;
    periods.forEach(function (p, i) {
      const value = Number(grade[p.dbField]) || 0;
      if (value === 0) hasUngraded[i] = true;
      totals[i] += value;
    });
  });

  const periodGrades = totals.map(function (total, i) {
    if (hasUngraded[i] || total === 0) return 0;
    return Math.round(total / componentGrades.length);
  });

  const lastPeriodGrade = periodGrades[periodGrades.length - 1];
  const finalGrade = lastPeriodGrade === 0
    ? 0
    : Math.round(periodGrades.reduce(function (a, b) { return a + b; }, 0) / periods.length);

  return { periodGrades: periodGrades, finalGrade: finalGrade };
}

/**
 * A learning area built from components that do not count equally.
 *
 * EPP/TLE is 25% ICT and 75% AFA/FCS/IA. mapehComposite cannot serve: it
 * averages its components evenly, which is right for MAPEH and wrong here.
 *
 * A period is zeroed when any component is missing a mark for it, the same
 * rule MAPEH follows - three quarters of an answer is not a grade - and the
 * weights are renormalised over whichever components the section actually
 * teaches, so a school running only one of them still gets that component's
 * mark rather than a quarter of it.
 *
 * @param {Array} components  [{ grade, weight }], grade may be null
 * @returns {{ periodGrades: number[], finalGrade: number }}
 */
export function weightedComposite(components, periods) {
  const empty = {
    periodGrades: periods.map(function () { return 0; }),
    finalGrade: 0,
  };

  const present = (components || []).filter(function (c) { return c && c.grade; });
  if (present.length === 0) return empty;

  const totalWeight = present.reduce(function (sum, c) {
    return sum + (Number(c.weight) || 0);
  }, 0);
  if (totalWeight === 0) return empty;

  const periodGrades = periods.map(function (p) {
    let total = 0;
    let complete = true;

    present.forEach(function (c) {
      const value = Number(c.grade[p.dbField]) || 0;
      if (value === 0) complete = false;
      total += value * (Number(c.weight) || 0);
    });

    return complete ? Math.round(total / totalWeight) : 0;
  });

  const last = periodGrades[periodGrades.length - 1];
  const finalGrade = last === 0
    ? 0
    : Math.round(periodGrades.reduce(function (a, b) { return a + b; }, 0) / periods.length);

  return { periodGrades: periodGrades, finalGrade: finalGrade };
}

/**
 * Conduct's own final, averaged over the scheme's periods. Returns "" — not 0 —
 * while the last period is ungraded, matching what the card prints.
 */
export function conductFinalGrade(classConduct, periods) {
  if (!classConduct || !periods || periods.length === 0) return "";

  const lastField = periods[periods.length - 1].conductField;
  if (!Number(classConduct[lastField])) return "";

  const total = periods.reduce(function (sum, p) {
    return sum + (Number(classConduct[p.conductField]) || 0);
  }, 0);

  return Math.round(total / periods.length);
}

/**
 * General average: subjects 90%, conduct 10%. Returns "INC" when the student is
 * missing a grade for any expected subject.
 *
 * expectedCount is how many subjects the curriculum template calls for;
 * finalGrades is what was actually found. They differing is the incomplete
 * signal.
 */
export function generalAverage(finalGrades, conductFinal, expectedCount, clubFinal) {
  if (!finalGrades || finalGrades.length === 0) return "INC";

  const total = finalGrades.reduce(function (a, b) { return a + b; }, 0);
  const subjectsAverage = total / finalGrades.length;

  if (expectedCount !== finalGrades.length) return "INC";
  return Math.round(blend(subjectsAverage, conductFinal, clubFinal));
}

/**
 * The school's weighting: academic 90%, conduct 5%, club 5%.
 *
 * An absent conduct or club mark contributes 0 rather than being weighted out.
 * That is deliberate and was decided by the registrar knowing what it costs: a
 * learner in no club scores nothing for that 5%, so their general average is
 * up to five points lower than a club member's on identical subject marks.
 */
function blend(academicAverage, conductMark, clubMark) {
  const conduct = Number(conductMark) || 0;
  const club = Number(clubMark) || 0;

  return academicAverage * 0.9 + conduct * 0.05 + club * 0.05;
}

/**
 * One term's general average, on the same weights as the final one.
 *
 * Blank until every subject the card expects has a mark for that term. A term
 * average over half the subjects is not an average of anything, and the final
 * general average has always held itself back the same way - it reads INC
 * rather than averaging what happens to be there.
 *
 * @param {number[]} subjectMarks   this term's marks, counted subjects only
 * @param {number}   expectedCount  how many the curriculum calls for
 * @param {number}   conductMark    this term's conduct
 * @param {number}   clubMark       this term's club mark, 0 if none
 */
export function termAverage(subjectMarks, expectedCount, conductMark, clubMark) {
  const marks = (subjectMarks || []).filter(function (m) { return Number(m) > 0; });
  if (marks.length === 0 || marks.length !== expectedCount) return "";

  const total = marks.reduce(function (a, b) { return a + Number(b); }, 0);
  return Math.round(blend(total / marks.length, conductMark, clubMark));
}

/**
 * Grade 12 keeps the 2016 curriculum's two-semester card. Under three terms a
 * semester can hold one term or two, so this divides by however many terms the
 * school year assigns to that semester rather than assuming two.
 *
 * Returns undefined while the semester's last term is ungraded, which is how
 * the caller knows to leave the cell blank.
 */
export function semesterAverage(grade, periods, semesterTerms) {
  if (!grade || !semesterTerms || semesterTerms.length === 0) return undefined;

  const fields = semesterTerms.map(function (index) {
    const period = periods.filter(function (p) { return p.index === index; })[0];
    return period ? period.dbField : null;
  }).filter(Boolean);

  if (fields.length === 0) return undefined;
  if (!Number(grade[fields[fields.length - 1]])) return undefined;

  const total = fields.reduce(function (sum, f) {
    return sum + (Number(grade[f]) || 0);
  }, 0);

  return Math.round(total / fields.length);
}

/** Conduct's semester average, same term-count rule as semesterAverage. */
export function semesterConduct(classConduct, periods, semesterTerms) {
  if (!classConduct || !semesterTerms || semesterTerms.length === 0) return "";

  const fields = semesterTerms.map(function (index) {
    const period = periods.filter(function (p) { return p.index === index; })[0];
    return period ? period.conductField : null;
  }).filter(Boolean);

  if (fields.length === 0) return "";
  if (!Number(classConduct[fields[fields.length - 1]])) return "";

  const total = fields.reduce(function (sum, f) {
    return sum + (Number(classConduct[f]) || 0);
  }, 0);

  return Math.round(total / fields.length);
}

/** Pass/fail as printed on the card. Blank until there is a final grade. */
export function passFailLabel(finalGrade, passingGrade) {
  if (!finalGrade || finalGrade === 0) return "";
  return finalGrade < passingGrade ? "Failed" : "Passed";
}
